import { Building, Lock, Store, Users } from '@digital-twins/design-system/icons'
import type { ReactNode } from 'react'

import Image from '@/routing/Image'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { AudienceSummary } from '@/lib/api'

/**
 * The fields the glyph reads. Audience list rows (`AudienceSummary`) and the
 * study-creation `Population` both carry them, with nullable text fields.
 */
export interface AudienceSharingGlyphAudience {
  sharingMode?: AudienceSummary['sharingMode']
  canManage?: boolean
  creatorName?: string | null
  creatorEmail?: string | null
  organizationName?: string | null
  publisherDomain?: string | null
  logoUrl?: string | null
}

export type AudienceSharingVariant = 'private' | 'shared' | 'organization' | 'marketplace'

export interface AudienceSharingPresentation {
  variant: AudienceSharingVariant
  tooltip: string
  logoUrl: string | null
}

interface AudienceSharingGlyphProps {
  audience: AudienceSharingGlyphAudience
  className?: string
  dataTestId?: string
}

function resolveCreatorLabel(audience: AudienceSharingGlyphAudience): string {
  return (
    audience.creatorName?.trim()
    || audience.creatorEmail?.trim()
    || audience.publisherDomain?.trim()
    || audience.organizationName?.trim()
    || 'Unknown'
  )
}

/** Mirrors the audience lobby card badge vocabulary for compact list surfaces. */
export function resolveAudienceSharingPresentation(
  audience: AudienceSharingGlyphAudience,
): AudienceSharingPresentation {
  const mode = audience.sharingMode ?? (audience.canManage === false ? 'shared' : 'private')

  if (mode === 'marketplace') {
    const publisher = audience.organizationName?.trim()
      || audience.publisherDomain?.trim()
      || 'Marketplace'
    return {
      variant: 'marketplace',
      tooltip: publisher,
      logoUrl: audience.logoUrl?.trim() || null,
    }
  }

  if (mode === 'organization' || mode === 'published') {
    if (audience.canManage) {
      return {
        variant: 'organization',
        tooltip: 'Shared with your organization',
        logoUrl: null,
      }
    }
    return {
      variant: 'shared',
      tooltip: `Shared by ${resolveCreatorLabel(audience)}`,
      logoUrl: null,
    }
  }

  if (mode === 'shared') {
    if (audience.canManage) {
      return {
        variant: 'shared',
        tooltip: 'Shared audience',
        logoUrl: null,
      }
    }
    return {
      variant: 'shared',
      tooltip: `Shared by ${resolveCreatorLabel(audience)}`,
      logoUrl: null,
    }
  }

  return {
    variant: 'private',
    tooltip: 'Private to your organization',
    logoUrl: null,
  }
}

export function AudienceSharingGlyph(props: AudienceSharingGlyphProps) {
  const { audience, className, dataTestId } = props
  const presentation = resolveAudienceSharingPresentation(audience)

  let glyph: ReactNode
  if (presentation.variant === 'marketplace' && presentation.logoUrl) {
    glyph = (
      <Image
        src={presentation.logoUrl}
        alt=""
        width={14}
        height={14}
        className="h-3.5 w-3.5 rounded-full object-cover"
      />
    )
  } else if (presentation.variant === 'marketplace') {
    glyph = <Store className="h-3.5 w-3.5" />
  } else if (presentation.variant === 'organization') {
    glyph = <Building className="h-3.5 w-3.5" />
  } else if (presentation.variant === 'shared') {
    glyph = <Users className="h-3.5 w-3.5" />
  } else {
    glyph = <Lock className="h-3.5 w-3.5" />
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={dataTestId ?? 'audience-sharing-glyph'}
          data-sharing-variant={presentation.variant}
          className={cn(
            'inline-flex shrink-0 items-center justify-center text-muted-foreground',
            presentation.variant === 'marketplace' && !presentation.logoUrl && 'rounded-full bg-muted p-0.5',
            className,
          )}
          aria-label={presentation.tooltip}
        >
          {glyph}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        {presentation.tooltip}
      </TooltipContent>
    </Tooltip>
  )
}
